import {
  Button as ChakraButton,
  Text as ChakraText,
  VStack
} from "@chakra-ui/react"
import Button from "@pathwright/ui/src/components/button/Button"
import { useTranslate } from "@pathwright/ui/src/components/lng/withTranslate"
import Modal from "@pathwright/ui/src/components/modal/Modal"
import Pathicon from "@pathwright/ui/src/components/pathicon/Pathicon"
import Portal from "@pathwright/ui/src/components/portal/Portal"
import Text from "@pathwright/ui/src/components/ui/Text"
import { ConnectStores } from "lib/core/store"
import PropTypes from "prop-types"
import styled from "styled-components"
import { LEARN } from "../../../../../path/constants"
import "./PathViewAs.css"

const StyledModal = styled(Modal)`
  .Modal__content {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .Modal__close {
    display: none;
  }
`

// Prompt user with a confirmation to either continue previewing path
// or switch to Learn mode. Only showing to users who have a Learn mode
// version of the path (are registered as a Moderator-).
function ConfirmPreview({ onSwitchToLearn, onSwitchToPreview, onCancel }) {
  const tPrefix = "path_preview.confirm"
  const { t, Trans } = useTranslate()

  return (
    <StyledModal onClose={onCancel}>
      <VStack bg="white" borderRadius="md" maxW="400px" p="20px" spacing={6}>
        <Pathicon icon="eye" style={{ fontSize: "2em" }} />
        <ChakraText textAlign="center">
          <Trans
            i18nKey={`${tPrefix}.<strong>Preview</strong> displays how a learner will see the latest changes to this Path but does not store your completion or progress.`}
            defaults="<strong>Preview</strong> displays how a learner will see the latest changes to this Path but does not store your completion or progress."
          />
        </ChakraText>
        <VStack spacing={0}>
          <ChakraButton
            colorScheme="brand"
            onClick={onSwitchToLearn}
            cursor="pointer"
          >
            {t(`${tPrefix}.Switch to Learn`)}
          </ChakraButton>
          <ChakraText
            size="xxs"
            color="gray.600"
            textAlign="center"
            fontStyle="italic"
          >
            {t(`${tPrefix}.To complete steps and make progress on this Path.`)}
          </ChakraText>
        </VStack>
        <VStack spacing={0}>
          <ChakraButton
            colorScheme="brand"
            onClick={onSwitchToPreview}
            cursor="pointer"
          >
            {t(`${tPrefix}.Continue with Preview`)}
          </ChakraButton>
          <ChakraText
            size="xxs"
            color="gray.600"
            textAlign="center"
            fontStyle="italic"
          >
            {t(
              `${tPrefix}.To preview the latest updates without tracking progress.`
            )}
          </ChakraText>
        </VStack>
      </VStack>
    </StyledModal>
  )
}

function PreviewBanner({ onExit }) {
  return (
    <Portal portalElementID="alert">
      <div className="PathViewAs" key="path-view-as">
        <Text.H4 inverted>Previewing path as learner...</Text.H4>{" "}
        <Button onClick={onExit} inverted={true}>
          Exit
          <Pathicon icon="eye-off" />
        </Button>
      </div>
    </Portal>
  )
}

const PathViewAs = ({
  viewingAsLearner,
  viewingAsLearnerPrompt,
  viewAsLearner,
  exitViewAsLearner,
  switchToLearn
}) => {
  if (!viewingAsLearner && !viewingAsLearnerPrompt) {
    return null
  }

  return (
    <>
      {viewingAsLearner && <PreviewBanner onExit={exitViewAsLearner} />}
      {viewingAsLearnerPrompt && (
        <ConfirmPreview
          onSwitchToLearn={switchToLearn}
          onSwitchToPreview={viewAsLearner}
          onCancel={exitViewAsLearner}
        />
      )}
    </>
  )
}

PathViewAs.displayName = "PathViewAs"

PathViewAs.propTypes = {
  viewingAsLearner: PropTypes.bool,
  exitViewAsLearner: PropTypes.func.isRequired
}

const mapStateToProps = function (state, ownProps) {
  const { viewAsLearner, exitViewAsLearner, setMode } = state.pathStore.action
  const {
    viewing_as_learner: viewingAsLearner,
    viewing_as_learner_confirm: viewingAsLearnerPrompt
  } = state.pathStore.state

  function switchToLearn() {
    exitViewAsLearner()
    setMode(LEARN)
  }

  return _.assign(
    {},
    {
      viewingAsLearner,
      viewingAsLearnerPrompt,
      viewAsLearner,
      exitViewAsLearner,
      switchToLearn
    },
    ownProps
  )
}

const stores = [
  { store: "path", watch: ["viewing_as_learner", "viewing_as_learner_confirm"] }
]

const ConnectedPathViewAs = ConnectStores(
  PathViewAs,
  stores,
  {},
  mapStateToProps
)

export default () => <ConnectedPathViewAs viewing_as_learner={false} />
